import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { useEffect, useCallback } from "react";
import { useHotkeyMode, type Hotkey } from "../../lib/hotkey-context";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { getKeyIcon } from "../../lib/keyboard";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";
import { ControllerIcon } from './controller-icon';
import { useGamepadNavigation } from "@/hooks/use-gamepad-navigation";
import { Kbd } from "./kbd";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold ring-offset-background  transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-[#121514] disabled:text-gray-400 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 duration-100",
  {
    variants: {
      variant: {
        default: "bg-[#D1F551] text-black hover:bg-white hover:text-black",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border text-lime  bg-background hover:bg-black/20 hover:text-white",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        blurred: "bg-gray-900 bg-opacity-[0.3]  hover:bg-muted/20 hover:text-white",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-5 rounded-md px-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  hotkey?: Hotkey;
  loading?: boolean;
  tooltip?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, hotkey, onClick, loading, tooltip, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const { mode } = useHotkeyMode();

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement> | MouseEvent) => {
      if (!props.disabled) {
        onClick?.(event as React.MouseEvent<HTMLButtonElement>);
      }
    }, [onClick, props.disabled]);

    useEffect(() => {
      if (!hotkey?.keyboard) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (props.disabled) return;
        const openDialog = document.querySelector('[data-state="open"][role="dialog"]');
        if (openDialog) return;

        const activeElement = document.activeElement;
        const isInputActive = activeElement instanceof HTMLElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable
        );

        const hasModifier = event.ctrlKey || event.metaKey || event.altKey || event.shiftKey;
        const isEscapeKey = event.key.toLowerCase() === 'escape';

        // Allow Escape key to work even when focused on inputs (for navigation/cancel actions)
        if ((!isInputActive || isEscapeKey) && !hasModifier && hotkey.keyboard?.toLowerCase() === event.key.toLowerCase()) {
          event.preventDefault();
          handleClick(new MouseEvent('click'));
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hotkey?.keyboard, handleClick, props.disabled]);

    useGamepadNavigation(
      hotkey?.controller ? {
        [hotkey.controller.toLowerCase()]: () => {
          if (!props.disabled) {
            handleClick(new MouseEvent('click'));
          }
        }
      } : undefined
    );

    const hotkeyIcon = hotkey && !hotkey.hideIcon && (
      <span className="text-xs opacity-80">
        {mode === 'keyboard' && hotkey.keyboard ? (
          <Kbd size={hotkey.size}>{hotkey.keyboard}</Kbd>
        ) : mode === 'controller' && hotkey.controller ? (
          <span>{<ControllerIcon button={hotkey.controller} />}</span>
        ) : null}
      </span>
    );

    const button = (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        onClick={handleClick}
        {...props}
      >
        {loading ? <>{props.children}<Loader2 className="h-4 w-4 animate-spin" /></> : props.children}
        {!hotkey?.hideIcon && hotkeyIcon}
      </Comp>
    );

    if (tooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {button}
            </TooltipTrigger>
            <TooltipContent>
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return button;
  }
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
